import { AlertTriangle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { navigate } from '../lib/navigation';
import { Sentry } from '../lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope((scope) => {
      scope.setTag('error.boundary', 'admin-panel-root');
      scope.setContext('react', { componentStack: errorInfo.componentStack });
      Sentry.captureException(error);
    });
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    navigate('/');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-danger/10">
            <AlertTriangle size={28} className="text-danger" />
          </div>
          <h2 className="text-xl font-semibold text-text">Something went wrong</h2>
          <p className="text-sm text-text-muted text-center max-w-md">
            An unexpected error occurred while loading this page. Try navigating back to the
            overview.
          </p>
          {this.state.error && import.meta.env.DEV && (
            <pre className="text-xs text-text-muted bg-surface border border-border rounded-lg p-3 max-w-lg overflow-auto max-h-32">
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleGoHome}
            className="mt-2 bg-primary text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-primary-hover transition-colors"
          >
            Go to Overview
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
